import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';

export const LACUNA_LOCAL_STORAGE_MOTD_NAME: string = 'ls-motd-hidden-ids';

export interface MotdState {
  hiddenMessageIds: number[];
  hiddenCount: number;
}

interface MotdHiddenStorage {
  hiddenMessageIds: number[];
}

@Injectable({
  providedIn: 'root'
})
export class MotdService {
  public motdState$ = new BehaviorSubject<MotdState>({
    hiddenMessageIds: [],
    hiddenCount: 0
  });

  public refreshMessages$ = new Subject<void>();

  constructor() {
    this.loadHiddenMessageIds();
  }

  getHiddenMessageIds(): number[] {
    return this.motdState$.value.hiddenMessageIds;
  }

  getHiddenCount(): number {
    return this.motdState$.value.hiddenCount;
  }

  hideMessages(messageIds: number[]): void {
    const currentState = this.motdState$.value;
    const newHiddenIds = new Set([...currentState.hiddenMessageIds, ...messageIds]);
    this.updateState(Array.from(newHiddenIds));
  }

  clearHiddenMessages(): void {
    this.updateState([]);
    this.refreshMessages$.next();
  }

  isMessageHidden(messageId: number): boolean {
    return this.motdState$.value.hiddenMessageIds.includes(messageId);
  }

  private updateState(hiddenIds: number[]): void {
    const newState: MotdState = {
      hiddenMessageIds: hiddenIds,
      hiddenCount: hiddenIds.length
    };

    this.motdState$.next(newState);
    this.saveToLocalStorage(hiddenIds);
  }

  private loadHiddenMessageIds(): void {
    const storedData = localStorage.getItem(LACUNA_LOCAL_STORAGE_MOTD_NAME);

    if (!storedData) {
      this.updateState([]);
      return;
    }

    try {
      const parsedData: MotdHiddenStorage = JSON.parse(storedData);
      const hiddenIds = parsedData.hiddenMessageIds || [];
      this.updateState(hiddenIds);
    } catch (error) {
      console.error('MOTD Service: Failed to parse hidden message IDs', error);
      this.updateState([]);
    }
  }

  private saveToLocalStorage(hiddenIds: number[]): void {
    try {
      const dataToStore: MotdHiddenStorage = {
        hiddenMessageIds: hiddenIds
      };
      localStorage.setItem(LACUNA_LOCAL_STORAGE_MOTD_NAME, JSON.stringify(dataToStore));
    } catch (error) {
      console.error('MOTD Service: Failed to save hidden message IDs', error);
    }
  }
}

export function clearMotdHiddenMessages(motdService: MotdService): void {
  motdService.clearHiddenMessages();
}
